import {loadClaimsAndProfile} from 'Core/components/Startup';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {sequenceT} from 'fp-ts/lib/Apply';
import * as E from 'fp-ts/lib/Either';
import {constant, flow} from 'fp-ts/lib/function';
import * as IO from 'fp-ts/lib/IO';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import qs from 'query-string';
import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {RouteComponentProps} from 'react-router-dom';
import ErrorPage from 'Shared/components/ErrorPage';
import Loading from 'Shared/components/Loading';
import {decodeMyAccessClaims, decodeToken, getAccessToken, saveAccessToken, Token} from 'Shared/utilities/Auth';
import {NoOp} from 'Shared/utilities/IOUtilities';
import {fromLocalStorage, LocalStorageKey} from 'Shared/utilities/localStorage';
import {safeParseInt} from 'Shared/utilities/parsers';
import {applicativeValidation, lift, liftT} from 'Shared/utilities/ToValidation';

type AuthorizeProps = RouteComponentProps;

const checkAccessToken = (accessToken: string) =>
  E.fromOption(constant('Access Token Is Missing'))(O.fromNullable(accessToken));

const convertExpiresInToNumber = (value: string) => {
  const result = E.fromOption(constant('Expires In Is Not A Number'));
  return result(safeParseInt(value));
};

const checkExpiresIn = (expiresIn: string) =>
  pipe(
    E.fromOption(constant('Expires In Value Is Missing'))(O.fromNullable(expiresIn)),
    E.chain(convertExpiresInToNumber)
  );

const checkState = (storedState: string | null) => (responseState: string | undefined) =>
  pipe(
    E.fromOption(constant('State Value Is Missing In Local Storage'))(O.fromNullable(storedState)),
    E.chain(() => E.fromOption(constant('State Value Is Missing In Response'))(O.fromNullable(responseState))),
    E.chain(
      E.fromPredicate(
        () => storedState === responseState,
        () => 'State Does Not Match'
      )
    )
  );

const validateTokenCanBeDecoded = flow(
  decodeToken,
  E.fromOption(() => 'Access Token Is Not In A Valid Format.')
);

const validateResponse = (response: Response) =>
  sequenceT(applicativeValidation)(
    lift(checkAccessToken)(response.access_token),
    liftT<number>()(checkExpiresIn)(response.expires_in),
    liftT<Token>()(validateTokenCanBeDecoded)(response.access_token),
    lift(checkState(fromLocalStorage(LocalStorageKey.State)()))(response.state)
  );

type Response = {
  access_token: string;
  state: string;
  expires_in: string;
};

export const Authorize = (props: AuthorizeProps) => {
  const dispatch = useDispatch();
  const {history} = props;
  const [errors, setErrors] = useState<string[]>([]);
  const response = qs.parse(window.location.href.slice(window.location.href.indexOf('#'))) as Response;
  const isRefreshingToken = O.isSome(getAccessToken());
  const validatedTokenInformation = validateResponse(response);

  useEffect(() => {
    const processValidations = pipe(
      validatedTokenInformation,
      E.fold(
        validationErrors => () => setErrors(validationErrors),
        ([accessToken, expiresIn, decodedToken]) => {
          const myAccessClaims = pipe(decodedToken, decodeMyAccessClaims);

          const handleRedirectionToApp = () => {
            if (!isRefreshingToken) {
              if (O.isSome(myAccessClaims) && myAccessClaims.value.hasAcceptedTermsAndConditions) {
                history.replace(CoreRouteConstants.TERMS_AND_CONDITIONS);
              } else {
                history.replace(CoreRouteConstants.DASHBOARD);
              }
            } else {
              window.top?.postMessage('timer', window.location.origin);
            }
          };

          const initializeMyAccessClaimsAndProfile = pipe(
            myAccessClaims,
            O.fold(() => NoOp, loadClaimsAndProfile(dispatch))
          );

          const initialize = sequenceT(IO.io)(
            saveAccessToken(accessToken, expiresIn, decodedToken),
            initializeMyAccessClaimsAndProfile
          );

          return pipe(initialize, IO.map(handleRedirectionToApp));
        }
      )
    );

    processValidations();
  }, []);

  return pipe(
    NEA.fromArray(errors),
    O.fold(
      () => <Loading />,
      validationErrors => (
        <ErrorPage
          error={{
            message: (
              <>
                The sign-on process has failed due to the following errors:
                <br />
                {validationErrors.map(error => (
                  <>
                    {error}
                    <br />
                  </>
                ))}
              </>
            ),
          }}
        />
      )
    )
  );
};

export default Authorize;
